// src/components/folder/folder-details-dialog/UserView.tsx
// ------------------------------------------------------------
import { useCallback, useMemo, useState } from 'react';
import { Check, MoreVertical, Plus, Trash, X } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/hooks/queries/auth';
import { useConfirmHandler } from '@/hooks/useConfirmHandler';
import {
  addGroupEditorMutation,
  removeGroupEditorMutation,
  useGetGroupAssignments,
} from '@/hooks/queries/folder';
import { useAddMembersToGroupMutation, useGetAllGroups } from '@/hooks/queries/groups';
import { Button } from '@ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/tooltip';
import { Avatar, AvatarFallback } from '@ui/avatar';
import { cn } from '@/lib/utils';
import { Badge } from '@ui/badge';
import type { ListItemAssignmentsOrEditors, User, UserDetails } from '@/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// ------------------------------------------------------------

/**
 * The UserItem component is a memoized component that displays a user item.
 * @returns A memoized component that displays a user item.
 */
const UserItem = ({
  user,
  folderId,
  groupId,
  showBellIcon = true,
  showEditorRoleControls = true,
  showMemberRoleControls = true,
  showLeaveButton = true,
  isAdmin = false,
  showAddNewMember = false,
  allowActions = true,
  setQuery,
  canAddMembersToGroup,
}: UserItemProps) => {
  const loggedInUser = useUser((state) => state.user);
  const [showConfirmAddMember, setShowConfirmAddMember] = useState<User | UserDetails | null>(null);

  /**
   * The function to check if the user is the logged in user
   */
  const isMe = useMemo(() => {
    return String(user.id) === String(loggedInUser?.id);
  }, [loggedInUser, user]);
  /**
   * The mutation to add an editor to the list item
   */
  const { mutateAsync: addEditorToGroup } = addGroupEditorMutation(folderId, groupId);

  /**
   * The mutation to remove an editor from the group
   */
  const removeEditorMutation = removeGroupEditorMutation(folderId, groupId);
  /**
   * The data of the group editors/subscribers
   */
  const { data: folderEditors } = useGetGroupAssignments(folderId, groupId);
  const isCurrentUserEditor = useMemo(() => {
    const folderEditorsData = folderEditors?.data;
    if (!folderEditorsData) return false;
    return folderEditorsData?.find(
      (editor) =>
        String(editor.user.id) === String(user.id) && editor.role.toLowerCase() === 'editor'
    );
  }, [folderEditors, user]);

  /**
   * The function to check if the logged in user is an editor
   */
  const isLoggedInUserEditor = useMemo(() => {
    const folderEditorsData = folderEditors?.data;
    if (!folderEditorsData) return false;
    console.log("folderEditorsData >>>>", folderEditorsData);
    return folderEditorsData?.find(
      (editor) =>
        String(editor.user.id) === String(loggedInUser?.id) &&
       ( editor.role.toLowerCase() === 'editor' || editor.role.toLowerCase() === 'admin' )
    );
  }, [folderEditors, loggedInUser]);

  /**
   * The function to get the readers
   */
  const readers = useMemo(() => {
    return folderEditors?.data?.filter(
      (editor) =>
        editor.role.toLowerCase() !== 'editor' && editor.role.toLowerCase() !== 'subscriber'
    );
  }, [folderEditors]);

  /**
   * The function to get the readers by object
   */
  const readersByObject = useMemo(() => {
    return readers?.reduce(
      (acc, reader) => {
        acc[reader.user.id] = reader;
        return acc;
      },
      {} as Record<string, ListItemAssignmentsOrEditors>
    );
  }, [readers]);


  /**
   * The function to handle the change of the editor role
   */
  const handleChangeEditorRole = useCallback(
    (value: string) => {
      if (value === 'editor') {
        // toast.promise(
          addEditorToGroup({
            userId: String(user.id),
            groupId: Number(groupId),
          }).catch((error) => {
            toast.error(error?.message || 'Failed to add editor to folder');
          });
      } else if (value === 'read') {
        // toast.promise(
          removeEditorMutation.mutateAsync({
            userId: String(user.id),
            groupId: Number(groupId),
          }).catch((error) => {
            toast.error(error?.message || 'Failed to remove editor from folder');
          });
      }
    },
    [addEditorToGroup, user, groupId, removeEditorMutation]
  );

  /**
   * The function to handle the change of the member role
   */
  const handleChangeMemberRole = useCallback(
    (value: string) => {
      if (value === 'editor') {
        // toast.promise(
          addEditorToGroup({
            userId: String(user.id),
            groupId: Number(groupId),
          }).catch((error) => {
            toast.error(error?.message || 'Failed to add editor to folder');
          });
      }
    },
    [addEditorToGroup, user, groupId]
  );

  /**
   * The function to handle the removal of the editor
   */
  const handleRemoveEditor = useCallback(async () => {
    try {
      await removeEditorMutation.mutateAsync({
        userId: String(user.id),
        groupId: Number(groupId),
      });
      // toast.success('Editor removed successfully');
    } catch (error) {
      toast.error('Failed to remove editor');
    }
  }, [removeEditorMutation, user, groupId]);

  const { mutateAsync: addMembersToGroup } = useAddMembersToGroupMutation(folderId);

  /**
   * The function to handle the addition of a new member
   */
  const handleAddNewMember = useCallback(
    async ({ member }: { memberId: string; member?: User | UserDetails }) => {
      setShowConfirmAddMember(member || null);
    },
    []
  );

  const handleConfirmAddMember = useCallback(async () => {
    const member = showConfirmAddMember;
    setShowConfirmAddMember(null);
    if (member) {
        // toast.promise(
      const response = await addMembersToGroup({
        groupId: String(groupId),
        userIds: [Number(member.id)],
      }).catch((error) => {
        toast.error(error?.message || 'Failed to add member to group');
      });
      if (response?.status) {
        setQuery?.('');
      }
    }
  }, [addMembersToGroup, groupId, setQuery, showConfirmAddMember]);

  const { data: groupDetails } = useGetAllGroups();
  const groupName = useMemo(() => {
    return groupDetails?.data?.find((group) => String(group.id) === String(groupId))?.name;
  }, [groupId, groupDetails]);

  /**
   * The component to display the user item
   */
  return (
    <div className="flex items-center justify-between rounded-md border p-2">
      <Dialog open={!!showConfirmAddMember} onOpenChange={() => setShowConfirmAddMember(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Benutzer zur Gruppe hinzufügen</DialogTitle>
          </DialogHeader>
          {showConfirmAddMember && (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                Sie möchten <span className="font-semibold">{showConfirmAddMember.firstName} {showConfirmAddMember.lastName}</span> als <span className="font-semibold">Viewer</span> zur {groupName} Gruppe hinzufügen.
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold">Wichtiger Hinweis:</span> Diese Person erhält dadurch Zugang zu allen Inhalten der Gruppe, nicht nur zu dem aktuellen Ordner.
              </div>
              <div className="text-sm">Möchten Sie fortfahren?</div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmAddMember(null)}
                  className="min-w-[110px]"
                >
                  <X className="h-4 w-4" />
                  Abbrechen
                </Button>
                <Button
                  variant="default"
                  onClick={handleConfirmAddMember}
                  className="min-w-[170px]"
                >
                  <Check className="h-4 w-4" />
                  Zur Gruppe hinzufügen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <UserDetailsWithAvatar user={user} />
      <div className="flex items-center gap-2">
        {isAdmin && (
          <Badge variant="default" className="text-xs font-medium">
            Admin
          </Badge>
        )}
        {showAddNewMember && allowActions ? (
          (isLoggedInUserEditor || canAddMembersToGroup) ? (
            <Button
              variant="outline"
              size="sm"
              className="w-auto px-2 text-xs font-medium"
              onClick={() => handleAddNewMember({ memberId: String(user.id), member: user })}
            >
              <Plus className="h-4 w-4" />
              Add member
            </Button>
          ) : (
            <span className="text-xs max-w-35 text-center font-medium">
              You need to be an editor to add members
            </span>
          )
        ) : null}
        {showEditorRoleControls ? (
          <Badge variant="default" className="text-xs font-medium">
            {isCurrentUserEditor ? 'Editor' : 'Reader'}
          </Badge>
        ) : null}
        {showMemberRoleControls ? (
          <Badge variant="default" className="text-xs font-medium">
            {readersByObject?.[user.id] ? 'Reader' : 'Reader'}
          </Badge>
        ) : null}
        {allowActions &&
          (Boolean(showEditorRoleControls && isLoggedInUserEditor) ||
            Boolean(showMemberRoleControls && isLoggedInUserEditor)) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="w-auto px-2"
                aria-label="Open role and notification actions"
              >
                <MoreVertical className="h-4 w-4 text-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {showEditorRoleControls && isLoggedInUserEditor ? (
                <DropdownMenuItem
                  onClick={() =>
                    handleChangeEditorRole(isCurrentUserEditor ? 'read' : 'editor')
                  }
                >
                  {isCurrentUserEditor ? 'Mark as reader' : 'Mark as editor'}
                </DropdownMenuItem>
              ) : null}
              {showMemberRoleControls && isLoggedInUserEditor ? (
                <DropdownMenuItem onClick={() => handleChangeMemberRole('editor')}>
                  Mark as editor
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {allowActions && isCurrentUserEditor && isMe && showLeaveButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-destructive/10 w-auto px-2"
                onClick={handleRemoveEditor}
                aria-label="Remove yourself as editor"
              >
                <Trash className="text-destructive h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remove yourself as editor</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
export default UserItem;

type UserItemProps = {
  user: User | UserDetails;
  folderId: string;
  groupId: string;
  showBellIcon?: boolean;
  showEditorRoleControls?: boolean;
  showMemberRoleControls?: boolean;
  showLeaveButton?: boolean;
  isAdmin?: boolean;
  showAddNewMember?: boolean;
  allowActions?: boolean;
  setQuery?: (query: string) => void;
  canAddMembersToGroup?: boolean;
};


type UserDetailsWithAvatarProps = {
  user: User | UserDetails;
  showAvatar?: boolean;
};

/**
 * The UserDetailsWithAvatar component is a memoized component that displays the user details with an avatar.
 * @returns A memoized component that displays the user details with an avatar.
 */
const UserDetailsWithAvatar = ({ user, showAvatar = true }: UserDetailsWithAvatarProps) => {
  /**
   * The current logged in user
   */
  const loggedInUser = useUser((state) => state.user);
  /**
   * The function to get the user name with the first word capitalized
   */
  const userNameWithFirstWordCapitalized = useMemo(() => {
    return (
      user.firstName?.charAt(0).toUpperCase() + user.firstName?.slice(1) ||
      user.email?.charAt(0).toUpperCase() + user.email?.slice(1) ||
      'Unknown'
    );
  }, [user]);

  /**
   * The function to check if the user is the logged in user
   */
  const isMe = useMemo(() => {
    return String(user.id) === String(loggedInUser?.id);
  }, [loggedInUser, user]);

  /**
   * The component to display the user details with an avatar
   */
  return (
    <div className="flex items-center gap-3">
      {showAvatar && (
        <Avatar className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
          <AvatarFallback
            className={cn(
              'text-xs font-medium',
              (!user.isEnabled || user.isBlocked) && 'line-through'
            )}
          >
            {userNameWithFirstWordCapitalized
              ?.split(' ')
              .map((word) => word.charAt(0))
              .join('')}
          </AvatarFallback>
        </Avatar>
      )}
      <div>
        <p className="text-sm font-medium">{isMe ? 'You' : userNameWithFirstWordCapitalized}</p>
        <p className="text-muted-foreground text-xs">{user.email}</p>
      </div>
    </div>
  );
};
